import { CSSProperties, ReactNode, useCallback, useMemo } from 'react';
import type {
  FindingAttribute,
  FindingGroup,
  FlagGroup,
} from '@/models/template';
import Panel from '@/features/report/shared/Panel/Panel';
import type { FindingStatement } from '@/models/report';
import FindingInput from './FindingInput';
import FindingGroupSection from './FindingGroupSection';
import FlagsBar from '../Flags/FlagsBar';
import {
  reconcileStatements,
  statementValues,
} from './FindingInput/utils/statements';
import {
  pooledStatements,
  splitPooledChange,
} from './FindingInput/utils/pooledStatements';
import styles from './FindingsPanel.module.css';

const byIndex = (a: number, b: number) => a - b;
const NO_STATEMENTS: FindingStatement[] = [];

interface FindingsPanelProps {
  // Core findings data
  findingGroups: FindingGroup[];
  findingAttributes: FindingAttribute[];
  findings: Record<string, FindingStatement[]>;

  // Finding change callback
  onFindingChange: (findingClass: string, value: FindingStatement[]) => void;

  // Flag props for integrated rendering
  flagGroups?: FlagGroup[];
  selectedFlags?: Set<string>;
  onFlagToggle?: (flagClass: string, checked: boolean) => void;

  // Alternative: slot for custom flag rendering (takes precedence)
  flagsSlot?: ReactNode;

  // Per-flag disable (e.g. role-based restrictions)
  disabledFlags?: Set<string>;
  disabledFlagHints?: Map<string, string>;

  /** The clips behind an AI statement of a finding class, or of any class a
   *  pooled group's input holds (AP-965). */
  clipsFor?: (findingClasses: string | string[], text: string) => string[];
  onOpenDicoms?: (dicomUuids: string[]) => void;

  // General state
  disabled?: boolean;
}

export default function FindingsPanel({
  findingGroups,
  findingAttributes,
  findings,
  onFindingChange,
  flagGroups,
  selectedFlags,
  onFlagToggle,
  flagsSlot,
  disabledFlags,
  disabledFlagHints,
  clipsFor,
  onOpenDicoms,
  disabled = false,
}: FindingsPanelProps) {
  // Edit-time normalization. After the reader makes a pick, check —
  // per finding_class — whether the resulting selection covers every
  // sub-attribute in that class with a NORMAL pill (and no non-NORMAL).
  // If yes, replace those sub-attribute NORMAL statements with the
  // single "All Normal" composite for that class.
  //
  // We iterate every class present in `findingAttributes`, not just the
  // `findingClass` arg, because multi-class non-SPLIT groups (e.g.
  // "Atria" containing LEFT_ATRIUM + RIGHT_ATRIUM) pool selections from
  // both classes into the FIRST class's stored value. Without per-class
  // iteration, RA's NORMAL pill never collapses — the wrapper would
  // only check LA's sub-attrs and find nothing matching RA's statement
  // text.
  const normalizedFindingChange = useCallback(
    (findingClass: string, next: FindingStatement[]) => {
      const lines = statementValues(next);
      if (lines.length === 0) {
        onFindingChange(findingClass, next);
        return;
      }

      const linesSet = new Set(lines);
      const classes = new Set(findingAttributes.map((a) => a.findingClass));
      let mutatedLines = [...lines];

      classes.forEach((cls) => {
        const subAttrs = findingAttributes.filter(
          (a) => a.findingClass === cls && a.label !== 'Normal'
        );
        const normalAttr = findingAttributes.find(
          (a) => a.findingClass === cls && a.label === 'Normal'
        );
        if (!normalAttr || subAttrs.length === 0) return;

        const perAttr = subAttrs.map((attr) => {
          const present = attr.statements.filter((s) => linesSet.has(s.text));
          return {
            hasNormal: present.some((s) => s.degree === 'NORMAL'),
            hasNonNormal: present.some((s) => s.degree !== 'NORMAL'),
          };
        });
        const everyHasNormal = perAttr.every((s) => s.hasNormal);
        const anyNonNormal = perAttr.some((s) => s.hasNonNormal);
        if (!everyHasNormal || anyNonNormal) return;

        const composite = [...normalAttr.statements]
          .filter((s) => s.degree === 'NORMAL')
          .sort(
            (a, b) =>
              (a.index ?? Number.MAX_SAFE_INTEGER) -
              (b.index ?? Number.MAX_SAFE_INTEGER)
          )[0];
        if (!composite) return;

        // Strip this class's sub-attribute NORMAL texts from the value
        // and insert the composite once.
        const subAttrNormalTexts = new Set(
          subAttrs.flatMap((a) =>
            a.statements.filter((s) => s.degree === 'NORMAL').map((s) => s.text)
          )
        );
        mutatedLines = mutatedLines.filter((l) => !subAttrNormalTexts.has(l));
        if (!mutatedLines.includes(composite.text)) {
          mutatedLines.push(composite.text);
        }
      });

      onFindingChange(findingClass, reconcileStatements(next, mutatedLines));
    },
    [findingAttributes, onFindingChange]
  );

  const valueFor = (findingClass: string): FindingStatement[] =>
    findings[findingClass] ?? NO_STATEMENTS;

  // Compute attributes map: findingClass -> FindingAttribute[]
  // Maps ALL findingClass values (not just the first per group) to support SPLIT rendering.
  // The API does not order groups or labels; `index` does.
  const orderedGroups = useMemo(
    () =>
      [...findingGroups]
        .sort((a, b) => byIndex(a.index ?? 0, b.index ?? 0))
        .map((g) => ({
          ...g,
          findingLabels: [...g.findingLabels].sort((a, b) =>
            byIndex(a.index, b.index)
          ),
        })),
    [findingGroups]
  );

  // Sorts each class's attributes by `index` so row order is deterministic
  // regardless of how the API orders them.
  const attributesMap = useMemo(() => {
    const map = new Map<string, FindingAttribute[]>();

    orderedGroups.forEach((group) => {
      group.findingLabels.forEach((label) => {
        const { findingClass } = label;
        if (findingClass && !map.has(findingClass)) {
          const attrs = findingAttributes
            .filter((attr) => attr.findingClass === findingClass)
            .sort((a, b) => byIndex(a.index, b.index));
          map.set(findingClass, attrs);
        }
      });
    });

    return map;
  }, [orderedGroups, findingAttributes]);

  // Determine header controls content
  const headerControls = useMemo(() => {
    // Slot takes precedence
    if (flagsSlot) return flagsSlot;

    // Use FlagsBar if flag props are provided
    if (flagGroups && selectedFlags && onFlagToggle) {
      return (
        <FlagsBar
          flagGroups={flagGroups}
          selectedFlags={selectedFlags}
          onFlagToggle={onFlagToggle}
          disabled={disabled}
          disabledFlags={disabledFlags}
          disabledFlagHints={disabledFlagHints}
        />
      );
    }

    return null;
  }, [
    flagsSlot,
    flagGroups,
    selectedFlags,
    onFlagToggle,
    disabled,
    disabledFlags,
    disabledFlagHints,
  ]);

  return (
    <Panel title="Findings">
      <div className={styles.header}>
        {headerControls && (
          <div className={styles.controls}>{headerControls}</div>
        )}
      </div>

      <div className={styles.list}>
        {orderedGroups.length === 0 ? (
          <div className={styles.emptyState}>
            No finding groups configured in template.
          </div>
        ) : (
          <div
            className={styles.grid}
            // Column-first: the stylesheet flows down each column, and needs
            // the row count to know where the second column starts.
            style={
              {
                '--finding-rows': Math.ceil(orderedGroups.length / 2),
              } as CSSProperties
            }
          >
            {(() => {
              const renderGroup = (group: FindingGroup) => {
                if (group.style === 'SPLIT') {
                  return (
                    <FindingGroupSection
                      key={group.uuid}
                      title={group.label ?? ''}
                    >
                      {group.findingLabels.map((findingLabel) => (
                        <FindingInput
                          key={findingLabel.uuid}
                          label={findingLabel.label}
                          value={valueFor(findingLabel.findingClass)}
                          onChange={(val) =>
                            normalizedFindingChange(
                              findingLabel.findingClass,
                              val
                            )
                          }
                          attributes={
                            attributesMap.get(findingLabel.findingClass) ?? []
                          }
                          clipsFor={
                            clipsFor &&
                            ((s) =>
                              clipsFor(findingLabel.findingClass, s.value))
                          }
                          onOpenDicoms={onOpenDicoms}
                          disabled={disabled}
                        />
                      ))}
                    </FindingGroupSection>
                  );
                }

                const groupClasses = group.findingLabels.map(
                  (fl) => fl.findingClass
                );
                const findingClass = groupClasses[0] ?? '';
                const groupLabel =
                  group.label ?? group.findingLabels?.[0]?.label ?? '';
                const hasMultipleClasses = group.findingLabels.length > 1;

                // Merge attributes from all finding classes in the group
                const mergedAttributes = hasMultipleClasses
                  ? group.findingLabels.flatMap(
                      (fl) => attributesMap.get(fl.findingClass) ?? []
                    )
                  : (attributesMap.get(findingClass) ?? []);

                // Class labels for folder-level grouping in the autocomplete
                const classLabels = hasMultipleClasses
                  ? group.findingLabels.map((fl) => ({
                      findingClass: fl.findingClass,
                      label: fl.findingClass
                        .toLowerCase()
                        .replace(/_/g, ' ')
                        .replace(/\b\w/g, (c) => c.toUpperCase()),
                    }))
                  : undefined;

                return (
                  <FindingGroupSection key={group.uuid} title={groupLabel}>
                    <FindingInput
                      key={`input-${group.uuid}`}
                      label=""
                      value={pooledStatements(findings, groupClasses)}
                      onChange={(val) =>
                        splitPooledChange(findings, groupClasses, val).forEach(
                          ([cls, value]) => normalizedFindingChange(cls, value)
                        )
                      }
                      attributes={mergedAttributes}
                      clipsFor={
                        clipsFor && ((s) => clipsFor(groupClasses, s.value))
                      }
                      onOpenDicoms={onOpenDicoms}
                      disabled={disabled}
                      findingClassLabels={classLabels}
                    />
                  </FindingGroupSection>
                );
              };

              // One flat list; the grid reads it top-to-bottom per column on
              // wide screens and in plain order when it collapses to one.
              return orderedGroups.map(renderGroup);
            })()}
          </div>
        )}
      </div>
    </Panel>
  );
}
